import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, getDoc, getDocs, collection, query, where, serverTimestamp, addDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import './Login.css';

function Login({ mode = 'signin' }) {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp]           = useState(mode === 'signup');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');
  const [loading, setLoading]             = useState(false);

  useEffect(() => {
    if (showResetPassword) {
      document.title = 'Reset Password - Wixon & Cross Desk Suite';
    } else if (isSignUp) {
      document.title = 'Sign Up - Wixon & Cross Desk Suite';
    } else {
      document.title = 'Sign In - Wixon & Cross Desk Suite';
    }
  }, [isSignUp, showResetPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (showResetPassword) {
        // Password Reset
        await sendPasswordResetEmail(auth, email);
        setSuccess('Password reset email sent! Check your inbox.');
        setEmail('');
        setTimeout(() => {
          setShowResetPassword(false);
          setSuccess('');
        }, 3000);
        setLoading(false);
        return;
      }

      if (isSignUp) {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        // Create Firebase auth account
        const credential = await createUserWithEmailAndPassword(auth, email, password);

        // Check if this is the very first user - make them Admin automatically
        // Otherwise create profile with no role (Admin must assign)
        const usersSnap = await getDoc(doc(db, 'meta', 'stats'));
        const isFirstUser = !usersSnap.exists() || usersSnap.data()?.userCount === 0;

        await setDoc(doc(db, 'users', credential.user.uid), {
          email: credential.user.email,
          role: isFirstUser ? 'Admin' : '',   // First user becomes Admin
          disabled: false,
          createdAt: serverTimestamp()
        });

        // Track user count
        await setDoc(doc(db, 'meta', 'stats'), {
          userCount: (usersSnap.data()?.userCount || 0) + 1
        });

        // Notify all admins of new account (if not first user)
        if (!isFirstUser) {
          const adminsQuery = query(collection(db, 'users'), where('role', '==', 'Admin'));
          const adminsSnap = await getDocs(adminsQuery);
          
          for (const adminDoc of adminsSnap.docs) {
            await addDoc(collection(db, 'notifications'), {
              userId: adminDoc.id,
              type: 'new_account',
              message: `New account created: ${credential.user.email}`,
              defectId: null,
              defectTitle: null,
              read: false,
              dismissed: false,
              createdAt: serverTimestamp()
            });
          }
        }

      } else {
        // Sign In - check if disabled
        const credential = await signInWithEmailAndPassword(auth, email, password);
        const profileSnap = await getDoc(doc(db, 'users', credential.user.uid));
        if (profileSnap.exists() && profileSnap.data()?.disabled) {
          await auth.signOut();
          setError('This account has been disabled. Please contact an Admin.');
          setLoading(false);
          return;
        }
        // Navigate to desktop on successful sign in
        navigate('/desktop');
      }
    } catch (err) {
      console.error('Auth error:', err);
      switch (err.code) {
        case 'auth/invalid-email':         setError('Invalid email address'); break;
        case 'auth/user-not-found':        setError('No account found with this email'); break;
        case 'auth/wrong-password':        setError('Incorrect password'); break;
        case 'auth/invalid-credential':    setError('Incorrect email or password'); break;
        case 'auth/email-already-in-use':  setError('An account with this email already exists'); break;
        case 'auth/weak-password':         setError('Password is too weak'); break;
        default: setError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Wixon & Cross LLC</h1>
          <h2>Defect Tracking System</h2>
        </div>

        <div className="login-form-container">
          <h3>{showResetPassword ? 'Reset Password' : (isSignUp ? 'Create Account' : 'Sign In')}</h3>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="your.email@example.com" autoComplete="email" />
            </div>

            {!showResetPassword && (
              <>
                <div className="form-group">
                  <label>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    required placeholder={isSignUp ? 'At least 6 characters' : 'Enter your password'}
                    autoComplete={isSignUp ? 'new-password' : 'current-password'} />
                </div>

                {isSignUp && (
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input type="password" value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required placeholder="Re-enter your password" autoComplete="new-password" />
                  </div>
                )}
              </>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Please wait...' : (showResetPassword ? 'Send Reset Link' : (isSignUp ? 'Create Account' : 'Sign In'))}
            </button>
          </form>

          <div className="login-toggle">
            {showResetPassword ? (
              <p>
                Remember your password?{' '}
                <button onClick={() => { setShowResetPassword(false); setError(''); setSuccess(''); }} className="link-button">
                  Back to Sign In
                </button>
              </p>
            ) : isSignUp ? (
              <p>Already have an account?{' '}
                <button onClick={() => navigate('/signin')} className="link-button">Sign In</button>
              </p>
            ) : (
              <>
                <p>Don't have an account?{' '}
                  <button onClick={() => navigate('/account-requests')} className="link-button">Request Access</button>
                </p>
                <p style={{marginTop: '0.5rem'}}>
                  Forgot your password?{' '}
                  <button onClick={() => { setShowResetPassword(true); setError(''); }} className="link-button">
                    Reset Password
                  </button>
                </p>
              </>
            )}
          </div>

          {isSignUp && (
            <div className="signup-note">
              Note: Self-signup is disabled. Please request access via the Request Access link.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
